import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { tpiHighlights } from '../../data/mock';
import useReveal from '../../hooks/useReveal';

/**
 * The Tenses Proficiency Index.
 *
 * The argument here is specificity: eight separately measured dimensions,
 * versus the single vague grade most tools give. The visual is a radial profile
 * chart so "eight dimensions" is felt, not just read.
 *
 * No CEFR equivalence is claimed. CEFR levels are criterion-referenced and set
 * by expert standard-setting panels against can-do descriptors; TPI bands are
 * derived from our own scoring, so presenting one as the other would overstate
 * what the score has been validated against.
 */
const TPICertificationSection = () => {
  const ref = useReveal({ stagger: 90 });

  return (
    <section className="section-alt">
      <div ref={ref} className="section">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          {/* ----------------------------- Visual ---------------------------- */}
          <div className="reveal order-2 lg:order-1">
            <RadarProfile />
          </div>

          {/* ------------------------------ Copy ----------------------------- */}
          <div className="order-1 lg:order-2">
            <p className="eyebrow reveal">Certification</p>
            <h2 className="heading-2 reveal mt-3">
              One number that
              <br />
              actually says something
            </h2>
            <p className="body-large reveal mt-5">
              The Tenses Proficiency Index scores you from 0 to 1200 across eight separate
              dimensions - so a recruiter sees precisely where your English is strong and
              where it still needs work, not one blunt grade.
            </p>

            <div className="reveal mt-8 flex flex-wrap items-center gap-4">
              <Link to="/tpi" className="btn-primary">
                How TPI is scored
                <ArrowRight className="h-4 w-4" />
              </Link>
              <span
                className="inline-flex items-center gap-1.5 text-sm"
                style={{ color: 'var(--text-secondary)' }}
              >
                <ShieldCheck className="h-4 w-4" style={{ color: 'var(--accent-strong)' }} />
                Verifiable certificate link
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/** Eight-axis radial profile drawn from the TPI dimension scores. */
const RadarProfile = () => {
  const size = 380;
  const c = size / 2;
  const rMax = 132;
  const n = tpiHighlights.length;

  // Convert each dimension to a point on its axis (starting at 12 o'clock).
  const pointFor = (value, i, scale = 1) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    const r = (value / 150) * rMax * scale;
    return [c + r * Math.cos(angle), c + r * Math.sin(angle)];
  };

  const polygon = tpiHighlights.map((d, i) => pointFor(d.value, i).join(',')).join(' ');

  return (
    <div className="relative mx-auto w-full max-w-[440px]">
      <div
        aria-hidden="true"
        className="absolute -inset-6 -z-10 rounded-[40px] opacity-60 blur-3xl"
        style={{ background: 'radial-gradient(55% 50% at 50% 45%, rgba(52,89,164,0.2), transparent 70%)' }}
      />

      <div className="glass-panel p-7">
        <div className="mb-4 flex items-center justify-between">
          <p className="text-[0.7rem] font-semibold uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>
            Skill profile
          </p>
          <span className="tpi-badge" style={{ fontSize: '0.76rem', padding: '0.35rem 0.8rem' }}>
            TPI 985
          </span>
        </div>

        <svg viewBox={`0 0 ${size} ${size}`} className="w-full" role="img" aria-label="Eight-dimension TPI skill profile">
          <defs>
            <linearGradient id="radarFill" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4c80c2" stopOpacity="0.42" />
              <stop offset="100%" stopColor="#8FEC78" stopOpacity="0.3" />
            </linearGradient>
          </defs>

          {/* Concentric guides */}
          {[0.25, 0.5, 0.75, 1].map((s) => (
            <polygon
              key={s}
              points={tpiHighlights.map((_, i) => pointFor(150, i, s).join(',')).join(' ')}
              fill="none"
              stroke="rgba(14,21,34,0.07)"
              strokeWidth="1"
            />
          ))}

          {/* Axes */}
          {tpiHighlights.map((_, i) => {
            const [x, y] = pointFor(150, i);
            return <line key={i} x1={c} y1={c} x2={x} y2={y} stroke="rgba(14,21,34,0.06)" strokeWidth="1" />;
          })}

          {/* The profile itself */}
          <polygon points={polygon} fill="url(#radarFill)" stroke="var(--tenses-blue)" strokeWidth="2.5" strokeLinejoin="round" />

          {/* Vertex dots */}
          {tpiHighlights.map((d, i) => {
            const [x, y] = pointFor(d.value, i);
            return <circle key={d.name} cx={x} cy={y} r="4" fill="#fff" stroke="var(--tenses-blue)" strokeWidth="2.5" />;
          })}

          {/* Labels */}
          {tpiHighlights.map((d, i) => {
            const [x, y] = pointFor(150, i, 1.19);
            return (
              <text
                key={d.name}
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ fontSize: 11.5, fontWeight: 500, fill: 'var(--text-secondary)' }}
              >
                {d.name}
              </text>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

export default TPICertificationSection;
